import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { adminAPI } from '../../../services/api';
import './AdminReplyForm.css';

const AdminReplyForm = ({ forum, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    reply_text: '',
    attachments: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewUrls, setPreviewUrls] = useState([]);

  // Cleanup preview URLs on component unmount
  useEffect(() => {
    return () => {
      previewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const files = Array.from(e.dataTransfer.files);
      handleFiles(files);
    }
  };

  const handleFiles = (files) => {
    const validFiles = [];
    const errors = [];
    
    files.forEach(file => {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        errors.push(`${file.name}: Only image files are allowed`);
        return;
      }
      
      // Validate file size (2MB)
      if (file.size > 2 * 1024 * 1024) {
        errors.push(`${file.name}: File size must be less than 2MB`);
        return;
      }
      
      // Check if already selected
      if (formData.attachments.some(f => f.name === file.name && f.size === file.size)) {
        errors.push(`${file.name}: File already selected`);
        return;
      }
      
      // Check maximum files (5)
      if (formData.attachments.length + validFiles.length >= 5) {
        errors.push('Maximum 5 files allowed');
        return;
      }
      
      validFiles.push(file);
    });
    
    // Add valid files to attachments
    if (validFiles.length > 0) {
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...validFiles]
      }));
      
      // Create preview URLs
      const newPreviewUrls = validFiles.map(file => URL.createObjectURL(file));
      setPreviewUrls(prev => [...prev, ...newPreviewUrls]);
    }
    
    // Show errors if any
    if (errors.length > 0) {
      setError(errors.join(', '));
    }
  };

  const removeAttachment = (index) => {
    // Revoke the preview URL to prevent memory leaks
    if (previewUrls[index]) {
      URL.revokeObjectURL(previewUrls[index]);
    }
    
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
    
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formDataToSend = new FormData();
      // Only append reply_text if it has content
      if (formData.reply_text && formData.reply_text.trim()) {
        formDataToSend.append('reply_text', formData.reply_text.trim());
      }
      
      formData.attachments.forEach((file) => {
        formDataToSend.append('attachments[]', file);
      });

      const response = await adminAPI.replyToForum(forum.id, formDataToSend);

      if (response.success) {
        // Clear preview URLs
        previewUrls.forEach(url => URL.revokeObjectURL(url));
        setPreviewUrls([]);
        onSuccess();
      } else {
        setError(response.message || 'Failed to post admin reply');
      }
    } catch (err) {
      console.error('Error posting admin reply:', err);
      if (err.data?.message) {
        setError(err.data.message);
      } else if (err.data?.errors) {
        const errorMessages = Object.values(err.data.errors).flat();
        setError(errorMessages.join(', '));
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to post admin reply. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="admin-reply-overlay">
      <div className="admin-reply-modal">
        <div className="admin-reply-header">
          <h2>Admin Reply</h2>
          <button 
            className="close-btn"
            onClick={onClose}
            type="button"
          >
            ×
          </button>
        </div>

        {forum && (
          <div className="forum-context">
            <h3>Replying to:</h3>
            <p className="forum-question">{forum.description}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="admin-reply-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="reply_text">Reply Text</label>
            <textarea
              id="reply_text"
              name="reply_text"
              value={formData.reply_text}
              onChange={handleInputChange}
              placeholder="Enter your admin reply..."
              rows="6"
              className="form-textarea"
            />
            <small className="form-help">
              Provide a helpful response to the user's question.
            </small>
          </div>

          <div className="form-group">
            <label>Attachments (Optional)</label>
            <div 
              className={`file-drop-zone ${dragActive ? 'drag-active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => document.getElementById('admin-file-input').click()}
            >
              <div className="drop-zone-content">
                <div className="drop-zone-icon">📎</div>
                <p>Drag and drop files here, or click to select</p>
                <small>Supported: Images only (JPEG, PNG, JPG, WEBP) - Max 2MB each, 5 files max</small>
              </div>
              <input
                id="admin-file-input"
                type="file"
                multiple
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleFileChange}
                className="file-input"
                style={{ display: 'none' }}
              />
            </div>
          </div>

          {formData.attachments.length > 0 && (
            <div className="attachments-preview">
              <h4>Selected Files:</h4>
              <div className="attachment-list">
                {formData.attachments.map((file, index) => (
                  <div key={index} className="attachment-item">
                    <div className="attachment-content">
                      {file.type.startsWith('image/') ? (
                        <div className="attachment-image">
                          <img 
                            src={previewUrls[index]} 
                            alt={file.name} 
                            className="attachment-thumbnail"
                          />
                        </div>
                      ) : (
                        <div className="attachment-icon">
                          <i className="bi bi-file-earmark"></i>
                        </div>
                      )}
                      <div className="attachment-info">
                        <span className="attachment-name" title={file.name}>{file.name}</span>
                        <span className="attachment-size">{formatFileSize(file.size)}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="remove-attachment"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || (!formData.reply_text.trim() && formData.attachments.length === 0)}
            >
              {loading ? 'Posting...' : 'Post Reply'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminReplyForm;
